/**
 * CWR 2.2 / 2.4 — event catalog card (Events tab). Same card shell as the service
 * card plus the event variant: leads with date·time, shows spots, and a
 * "View Event" / "Join waitlist" CTA. CWR 2.4 promotes the card to a SINGLE
 * interactive control → the event detail page (incl. sold-out events; the detail
 * shows the sold-out state). Markup copied from the confirmed mockup.
 */
import type { KeyboardEvent } from 'react';
import type { HubEventDto } from '@/api/types';
import { FocalImage } from '@/components/FocalImage';
import { bookedBadgeLabel, isViewableBooking } from '@/utils/bookingStatus';
import { modalityLabel, eventPriceParts, eventSpotsLabel, formatEventDateTime } from './catalogFormat';
import { ArrowIcon, CalendarIcon, CheckIcon, ClockIcon, modalityIcon } from './catalogIcons';
import styles from './BookSessionPage.module.css';

const IMAGE_FALLBACK = 'linear-gradient(135deg, var(--bg-card-2), var(--bg-card))';

export interface EventCatalogCardProps {
  event: HubEventDto;
  /** CWR 2.4 — navigate to the event detail page. */
  onView?: (event: HubEventDto) => void;
}

export function EventCatalogCard({ event, onView }: EventCatalogCardProps) {
  const price = eventPriceParts(event.service.price);
  const spots = eventSpotsLabel(event.spotsRemaining);
  const interactive = !!onView;
  const activate = () => interactive && onView!(event);
  const bookingLabel = isViewableBooking(event.userBooking)
    ? bookedBadgeLabel(event.userBooking.status)
    : null;
  const accessibleName = bookingLabel
    ? `View booking: ${event.title} — ${bookingLabel}`
    : `View event: ${event.title}`;

  const spotsClass =
    spots.variant === 'low' ? styles.spotsLow : spots.variant === 'out' ? styles.spotsOut : '';

  return (
    <article
      className={`${styles.serviceCard} ${styles.eventCard}`}
      data-testid={`event-catalog-card-${event.id}`}
      {...(interactive
        ? {
            role: 'button',
            tabIndex: 0,
            'aria-label': accessibleName,
            onClick: activate,
            onKeyDown: (e: KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                activate();
              }
            },
          }
        : {})}
    >
      {event.imageUrl ? (
        <FocalImage
          src={event.imageUrl}
          alt=""
          className={styles.serviceImage}
          focalPointX={event.focalPointX}
          focalPointY={event.focalPointY}
          focalPointScale={event.focalPointScale}
          loading="lazy"
        />
      ) : (
        <div className={styles.serviceImage} style={{ backgroundImage: IMAGE_FALLBACK }} />
      )}
      <div className={styles.serviceContent}>
        <span className={styles.eventDate}>
          {CalendarIcon}
          {formatEventDateTime(event.startTime)}
        </span>
        <h2 className={styles.serviceTitle}>{event.title}</h2>
        {event.summary && <p className={styles.serviceDesc}>{event.summary}</p>}

        <div className={styles.serviceMeta}>
          <div className={styles.metaLine}>
            <span className={styles.metaPart}>
              <span className={styles.serviceMetaDivider} />
              <span className={`${styles.serviceMetaItem} ${styles.modality}`}>
                {modalityIcon(event.modality)}
                {modalityLabel(event.modality)}
              </span>
            </span>
            <span className={styles.metaPart}>
              <span className={styles.serviceMetaDivider} />
              <span className={`${styles.serviceMetaItem} ${spotsClass}`}>{spots.text}</span>
            </span>
            <span className={styles.metaPart}>
              <span className={styles.serviceMetaDivider} />
              <span className={`${styles.serviceMetaPrice} ${price.free ? styles.free : ''}`}>
                {price.free ? 'Free' : price.amount}
              </span>
            </span>
          </div>
        </div>

        {bookingLabel ? (
          <span className={`${styles.serviceCta} ${styles.ctaBooked}`}>
            <span className={styles.ctaStatus}>
              {event.userBooking!.status === 'CONFIRMED' ? CheckIcon : ClockIcon}
              {bookingLabel}
            </span>
            <span className={styles.ctaView}>
              View
              {ArrowIcon}
            </span>
          </span>
        ) : (
          // Always "View Event" → detail (where sold-out state is handled).
          <span className={styles.serviceCta}>
            View Event
            {ArrowIcon}
          </span>
        )}
      </div>
    </article>
  );
}
